import { useEffect, useState, useRef } from 'react';
import { AIIcon, type AIIconHandle } from '../AI-Icon';
import { useBrand } from '../../../context/BrandContext';
import './Toast.css';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
    id: string;
    message: string;
    type?: ToastType;
    duration?: number;
    /** Botón opcional bajo el mensaje; al pulsarlo se ejecuta y el toast se cierra. */
    action?: { label: string; onClick: () => void };
    onClose: (id: string) => void;
    index: number;
}

const TYPE_LABELS: Record<ToastType, string> = {
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
};

export default function Toast({ 
    id,
    message, 
    type = 'info', 
    duration = 5000, 
    action,
    onClose,
    index
}: ToastProps) {
    const [displayedText, setDisplayedText] = useState('');
    const [isTypingComplete, setIsTypingComplete] = useState(false);
    const aiIconRef = useRef<AIIconHandle>(null);
    const { brand } = useBrand();

    // Efecto typing con speaking
    useEffect(() => {
        aiIconRef.current?.startSpeaking();
        let currentIndex = 0;
        const typingSpeed = 30; // ms por caracter

        const typingInterval = setInterval(() => {
            if (currentIndex < message.length) {
                setDisplayedText(message.slice(0, currentIndex + 1));
                currentIndex++;
            } else {
                clearInterval(typingInterval);
                setIsTypingComplete(true);
                aiIconRef.current?.stopSpeaking();
            }
        }, typingSpeed);

        return () => clearInterval(typingInterval);
    }, [message]);

    // Auto-cerrar después del duration (solo cuando termina el typing)
    useEffect(() => {
        if (!isTypingComplete) return;

        const timer = setTimeout(() => {
            onClose(id);
        }, duration);

        return () => clearTimeout(timer);
    }, [id, duration, onClose, isTypingComplete]);

    // Calcular posición vertical: 20px inicial + (altura + padding) * índice
    const topPosition = 20 + (index * 120);

    return (
        <div 
            className="toast"
            style={{ top: `${topPosition}px` }}
            role="button"
            tabIndex={0}
            onClick={() => onClose(id)}
            onKeyDown={(e) => { if (e.key === 'Enter') onClose(id); }}
        >
            {/* Header */}
            <div className="toast-header">
                <div className="toast-header-left">
                    <AIIcon ref={aiIconRef} size={25} />
                    <span className="toast-ai-name">{brand.assistantName}</span>
                </div>
                <div className={`toast-header-right toast-type-${type}`}>
                    {TYPE_LABELS[type]}
                </div>
            </div>

            {/* Content */}
            <div className="toast-content">
                <span className="toast-message">
                    {displayedText}
                    {!isTypingComplete && <span className="toast-cursor">|</span>}
                </span>
                {action && (
                    <button
                        type="button"
                        className="toast-action"
                        onClick={(e) => { e.stopPropagation(); action.onClick(); onClose(id); }}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        {action.label}
                    </button>
                )}
            </div>
        </div>
    );
}
